import { getAuthorizationToken } from '../AzureADConfig';
import { appConstants } from '../shared/constants';
import { handleResponse } from '../shared/handleResponse';

export interface IHttpService {
  get<T>(url: string, headersToAdd: Headers | undefined): Promise<T>;
  getWithBody<T>(
    url: string,
    body:
      | string
      | FormData
      | Blob
      | ArrayBufferView
      | ArrayBuffer
      | URLSearchParams
      | ReadableStream<Uint8Array>
      | null
      | undefined,
    headersToAdd: Headers | undefined,
  ): Promise<T>;
  post(
    url: string,
    body:
      | string
      | FormData
      | Blob
      | ArrayBufferView
      | ArrayBuffer
      | URLSearchParams
      | ReadableStream<Uint8Array>
      | null
      | undefined,
    headersToAdd: Headers | undefined,
    isFormData: boolean | undefined,
  ): Promise<Response>;

  postGeneric<T>(
    url: string,
    body:
      | string
      | FormData
      | Blob
      | ArrayBufferView
      | ArrayBuffer
      | URLSearchParams
      | ReadableStream<Uint8Array>
      | null
      | undefined,
    headersToAdd: Headers | undefined,
    isFormData: boolean | undefined,
  ): Promise<T>;

  put(
    url: string,
    body:
      | string
      | FormData
      | Blob
      | ArrayBufferView
      | ArrayBuffer
      | URLSearchParams
      | ReadableStream<Uint8Array>
      | null
      | undefined,
    headersToAdd: Headers | undefined,
    isFormData: boolean | undefined,
  ): Promise<Response>;

  putGeneric<T>(
    url: string,
    body:
      | string
      | FormData
      | Blob
      | ArrayBufferView
      | ArrayBuffer
      | URLSearchParams
      | ReadableStream<Uint8Array>
      | null
      | undefined,
    headersToAdd: Headers | undefined,
    isFormData: boolean | undefined,
  ): Promise<T>;

  delete(url: string, headersToAdd: Headers | undefined): Promise<Response>;
}

export class HttpService implements IHttpService {
  public async get<T>(
    url: string,
    headersToAdd: Headers | undefined,
  ): Promise<T> {
    const headers: Headers = await this.appendHeaders(
      headersToAdd,
      false,
      false,
    );

    return fetch(`${appConstants.apiUrl}/${url}`, {
      method: 'GET',
      headers,
    }).then(handleResponse);
  }

  public async getWithBody<T>(
    url: string,
    body:
      | string
      | FormData
      | Blob
      | ArrayBufferView
      | ArrayBuffer
      | URLSearchParams
      | ReadableStream<Uint8Array>
      | null
      | undefined,
    headersToAdd: Headers | undefined,
  ): Promise<T> {
    const headers: Headers = await this.appendHeaders(
      headersToAdd,
      false,
      false,
    );
    return fetch(`${appConstants.apiUrl}/${url}`, {
      method: 'GET',
      headers,
      body,
    }).then(handleResponse);
  }

  public async post(
    url: string,
    body:
      | string
      | FormData
      | Blob
      | ArrayBufferView
      | ArrayBuffer
      | URLSearchParams
      | ReadableStream<Uint8Array>
      | null
      | undefined,
    headersToAdd: Headers,
    isFormData: boolean | undefined,
  ): Promise<Response> {
    const headers: Headers = await this.appendHeaders(
      headersToAdd,
      true,
      isFormData,
    );

    return fetch(`${appConstants.apiUrl}/${url}`, {
      method: 'POST',
      headers,
      body,
    }).then(handleResponse);
  }

  public async postGeneric<T>(
    url: string,
    body:
      | string
      | FormData
      | Blob
      | ArrayBufferView
      | ArrayBuffer
      | URLSearchParams
      | ReadableStream<Uint8Array>
      | null
      | undefined,
    headersToAdd: Headers,
    isFormData: boolean | undefined,
  ): Promise<T> {
    const headers: Headers = await this.appendHeaders(
      headersToAdd,
      true,
      isFormData,
    );

    return fetch(`${appConstants.apiUrl}/${url}`, {
      method: 'POST',
      headers,
      body,
    }).then(handleResponse);
  }

  public async put(
    url: string,
    body:
      | string
      | FormData
      | Blob
      | ArrayBufferView
      | ArrayBuffer
      | URLSearchParams
      | ReadableStream<Uint8Array>
      | null
      | undefined,
    headersToAdd: Headers,
    isFormData: boolean | undefined,
  ): Promise<Response> {
    const headers: Headers = await this.appendHeaders(
      headersToAdd,
      true,
      isFormData,
    );

    return fetch(`${appConstants.apiUrl}/${url}`, {
      method: 'PUT',
      headers,
      body,
    }).then(handleResponse);
  }

  public async putGeneric<T>(
    url: string,
    body:
      | string
      | FormData
      | Blob
      | ArrayBufferView
      | ArrayBuffer
      | URLSearchParams
      | ReadableStream<Uint8Array>
      | null
      | undefined,
    headersToAdd: Headers,
    isFormData: boolean | undefined,
  ): Promise<T> {
    const headers: Headers = await this.appendHeaders(
      headersToAdd,
      true,
      isFormData,
    );

    return fetch(`${appConstants.apiUrl}/${url}`, {
      method: 'PUT',
      headers,
      body,
    }).then(handleResponse);
  }

  public async delete(
    url: string,
    headersToAdd: Headers | undefined,
  ): Promise<Response> {
    const headers: Headers = await this.appendHeaders(
      headersToAdd,
      false,
      false,
    );

    return fetch(`${appConstants.apiUrl}/${url}`, {
      method: 'DELETE',
      headers,
    }).then(handleResponse);
  }

  private readonly appendHeaders = async (
    headersToAdd: Headers | undefined,
    isPost: boolean,
    isFormData: boolean | undefined,
  ): Promise<Headers> => {
    const headers: Headers = new Headers();

    if (headersToAdd !== undefined) {
      const headersIterator: IterableIterator<[string, string]> =
        headersToAdd.entries();
      let result: IteratorResult<[string, string]> = headersIterator.next();
      while (!result.done) {
        headers.append(result.value[0], result.value[1]);
        result = headersIterator.next();
      }
    }

    const token = await getAuthorizationToken();

    headers.append('Authorization', `Bearer ${token}`);

    if (isPost && !isFormData) {
      headers.append('Accept', 'application/json; text/javascript, */*');
      headers.append('Content-Type', 'application/json; charset=UTF-8');
    }

    return headers;
  };
}
